import React, { Suspense, lazy } from "react";
import { useEffect } from "react";
import { Outlet, Route, Routes as ReactRoutes, useLocation } from "react-router-dom";
import { useSafeLocation } from "../hooks/useSafeRouter";
import { newrelic } from "../../utils/newrelic";


// Hooks
import { useUser } from "../hooks/useUser";
import useIsMobile from "../hooks/useIsMobile";
import { useNavigation } from "../hooks/useNavigation";

// Routes & Utils
import { PrivateRoute } from "./PrivateRoute";
import QueryParamPersistor from './QueryParameterPersistence';

// UI Components
import { LoadingSpinner } from "../ui/loading-spinner";
import { Header } from "@/components/navigation/Header";

// Eagerly loaded components (critical path)
import HomePage from "../pages/Home/home";
import RenderContentPage from "../pages/ContentPages/main";
import { NotFound } from "../pages/ErrorPages/Status";
//import AboutUs from "../pages/AboutUs/aboutus";

// Lazy loaded components (code splitting)
const AboutUs = lazy(() => import("../pages/AboutUs/AboutUs"));
const ContactUs = lazy(() => import("../pages/ContactUs/ContactUs"));
// const Resources = lazy(() => import("../pages/Resources/Resources"));
const BlogItem = lazy(() => import("../pages/Resources/BlogItem"));
const Showroom = lazy(() => import("../pages/ShowroomTour/Showroom"));
// const Draft404 = lazy(() => import("../pages/ErrorPages/Draft404"));
const FormSubmitted = lazy(() => import("../elements/FormSubmitted"));


//  const SignInAuth0Callback = lazy(() => import("../navigation/singinout/SignInAuth0Callback"));

// Product Components (lazy)
const MainCategoryGridShowcase = lazy(() => import("../pages/Product/showcase/MainCategoryGridShowcase"));
const CategoryShowcaseGrid = lazy(() => import("../pages/Product/showcase/CategoryShowcaseGrid"));
const ShowcaseItemListShowcase = lazy(() => import("../pages/Product/showcase/ShowcaseItemListShowcase"));
const ShowcaseItemDetail = lazy(() => import("../pages/Product/showcase/ShowcaseItemDetail"));
const ShowcaseGrid = lazy(() => import("../pages/Product/showcase/ShowcaseGrid"));
const AllShowcaseItems = lazy(() => import("../pages/Product/showcase/AllShowcaseItems"));
const QuoteTablePage = lazy(() => import("../pages/Product/quote/QuoteTablePage"));


// Customer Portal Components (lazy)
const Orders = lazy(() => import("../pages/CustomerPortal/order"));
const Quotes = lazy(() => import("../pages/CustomerPortal/quote"));
const Invoices = lazy(() => import("../pages/CustomerPortal/invoice"));
const PriceList = lazy(() => import("../pages/CustomerPortal/pricelist"));
const CustomerPortalWelcome = lazy(() => import("../pages/CustomerPortal/CustomerPortalWelcome"));

// Shared Components (lazy)
const EmailSubscribe = lazy(() => import("../pages/Subscribe/EmailSubcribe"));
const Footer = lazy(() => import("../footer/footer"));
const SideMenu = lazy(() => import("../navigation/navigationexports").then(module => ({ default: module.SideMenu })));
// const EmailPreferenceForm = lazy(() => import("../footer/EmailPreferenceForm"));

// Dev Components
const TailWind = lazy(() => import("../../lib/designelements/main"));


const IndSchoolAndEducation = lazy(() => import("../pages/ContentPages/Industries/SchoolsEducation/main"));
const IndAgeCard = lazy(() => import("../pages/ContentPages/Industries/AgeCare/main"));
const IndHospitality = lazy(() => import("../pages/ContentPages/Industries/Hospitality/main"));
const IndJoineryAndKitchenManufacturing = lazy(() => import("../pages/ContentPages/Industries/KitchenJoinery/main"));
const IndBuildingConstruction = lazy(() => import("../pages/ContentPages/Industries/BuildingConstruction/main"));
const IndCorporateGovernment = lazy(() => import("../pages/ContentPages/Industries/CorporateGovernment/main"));
const IndSmallMediumBusiness = lazy(() => import("../pages/ContentPages/Industries/SmallMediumBusiness/main"));
const IndDesignAndArchitecture = lazy(() => import("../pages/ContentPages/Industries/DesignArchitecture/main"));
const IndFurnitureBedding = lazy(() => import("../pages/ContentPages/WhatWeDo/FurnitureBedding/main"));
const IndHeatingCooling = lazy(() => import("../pages/ContentPages/WhatWeDo/HeatingCooling/main"));
const IndSecureGuestCasting = lazy(() => import("../pages/ContentPages/WhatWeDo/SecureGuestCasting/main"));
const IndAppliances = lazy(() => import("../pages/ContentPages/WhatWeDo/Appliances/main"));
// const IndAppliances2 = lazy(() => import("../pages/ContentPages/WhatWeDo/Appliances/main2"));
// const IndAppliances3 = lazy(() => import("../pages/ContentPages/WhatWeDo/Appliances/main3"));
// const IndAppliances4 = lazy(() => import("../pages/ContentPages/WhatWeDo/Appliances/main4"));
// const IndAppliances5WebpDarker = lazy(() => import("../pages/ContentPages/WhatWeDo/Appliances/main5WebpDarker"));

const IndTurnKey = lazy(() => import("../pages/ContentPages/WhatWeDo/TurnKey/main"));
const IndBathrooms = lazy(() => import("../pages/ContentPages/WhatWeDo/Bathrooms/main"));
const IndKitchen = lazy(() => import("../pages/ContentPages/WhatWeDo/Kitchen/main"));

const IndBlogs = lazy(() => import("../pages/Resources/Blogs"));
const IndCaseStudies = lazy(() => import("../pages/Resources/CaseStudies"));

const BlogRedirect = lazy(() => import("../pages/Resources/BlogCaseStudiesRedirect"));

//Footer Components
// const PrivacyPolicy = lazy(()=> import("../pages/LegalDocuments/PrivacyPolicy"));
const TermsOfUse = lazy(() => import("../pages/LegalDocuments/TermsofUse"));
const RetailReturnPolicy = lazy(() => import("../pages/LegalDocuments/RetailReturnPolicy"));
const DeliveryEzyTermsConditions = lazy(() => import("../pages/LegalDocuments/DeliveryEzyTermsConditions"));



export default function Routes() {
  const { data: nav } = useNavigation();
  const { data: user } = useUser();
  const location = useSafeLocation();
  const routerLocation = useLocation();
  const isCustomerPortal = location.pathname.startsWith("/customer-portal");
  const isMobileScreen = useIsMobile();

  // Derive page title from navigation data or URL path
  const getPageTitle = (pathname: string): string => {
    if (pathname === '/') return 'Home';

    // Look up in navigation data
    if (nav?.results) {
      const normalizedPath = pathname.replace(/\/$/, '');
      for (const item of nav.results) {
        const itemPath = item.url.replace(/\/$/, '');
        if (itemPath === normalizedPath) return item.name;
        for (const sub of item.sublinks || []) {
          const subPath = `${item.url}/${sub.url}`.replace(/\/$/, '');
          if (subPath === normalizedPath) return sub.name;
          for (const child of sub.sublinks || []) {
            const childPath = `${item.url}/${sub.url}/${child.url}`.replace(/\/$/, '');
            if (childPath === normalizedPath) return child.name;
          }
        }
      }
    }

    // Fallback: derive from URL slug
    const slug = pathname.replace(/^\/|\/$/g, '').split('/').pop() || '';
    return slug
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // Log route changes and update page title
  useEffect(() => {
    // console.log("URL changed:", routerLocation.pathname);
    // window.scrollTo(0, 0);
    if (!routerLocation.hash) {
      window.scrollTo(0, 0);
    }

    // Update browser tab title dynamically from nav data
    const pageTitle = getPageTitle(routerLocation.pathname);
    if (pageTitle) {
      document.title = `${pageTitle} | Harvey Norman Commercial NZ`;
    }

    try {
      const pagePath = routerLocation.pathname;
      const query = window.location.search || "";

      newrelic.setPageViewName?.(pagePath);
      newrelic.setCustomAttribute?.("app.Query", query);
      newrelic.setCustomAttribute?.("app.Page", pagePath);
      newrelic.setApplicationVersion?.(process.env.REACT_APP_VERSION || "");

      // Debug-only console output (shows agent presence and values)
      const debug = process.env.NODE_ENV === "development" || window.location.search.includes("version=dev");
      if (debug) {
        // safe check for global agent
        // eslint-disable-next-line no-console
        console.log("NewRelic debug:", {
          agentLoaded: !!(window as any).newrelic,
          pagePath,
          query,
          version: process.env.REACT_APP_VERSION || "",
        });
      }
    } catch (e) {
      // swallow telemetry errors
    }
  }, [routerLocation.pathname, nav]);

  return (
    <>
      <Header
        user={user}
        isCustomerPortal={isCustomerPortal}
        isMobileScreen={isMobileScreen}
      />

      {/* Main Content Area */}
      <div className={`flex overflow-hidden h-full no-scrollbar   ${location.pathname === '/' ? '' : 'mt-[4rem]'}`}>
        <div id="parent" className="contents z-10">
          <QueryParamPersistor />

          {/* Route Definitions with Suspense */}
          <Suspense fallback={<LoadingSpinner fullScreen message="Loading page..." />}>
            <ReactRoutes>
              {/* Development Routes */}
              <Route path="/_dev/v2/" element={<HomePage />} />
              <Route path="/_dev/design-elements/" element={<TailWind />} />

              {/* Public Routes */}

              <Route path="/" element={<HomePage />} />
              <Route path="/contact-us/" element={<ContactUs />} />
              <Route path="/about-us/" element={<AboutUs />} />
              <Route path="/form-submitted/" element={<FormSubmitted />} />
              {/* <Route path="/contact-us" element={<ContactUs />} />
              <Route path="/about-us" element={<AboutUs />} />
              <Route path="/form-submitted" element={<FormSubmitted />} /> */}


              {/* auth0 */}
              {/* <Route path="/signin-auth0" element={<SignInAuth0Callback />} /> */}

              {/* Content Pages  */}
              <Route path="/schools-and-education/" element={<IndSchoolAndEducation />} />
              <Route path="/retirement-and-aged-care/" element={<IndAgeCard />} />
              <Route path="/hospitality-and-accommodation/" element={<IndHospitality />} />
              <Route path="/joinery-and-kitchen-manufacturing/" element={<IndJoineryAndKitchenManufacturing />} />
              <Route path="/building-and-construction/" element={<IndBuildingConstruction />} />
              <Route path="/corporate-and-government/" element={<IndCorporateGovernment />} />
              <Route path="/small-medium-business/" element={<IndSmallMediumBusiness />} />
              <Route path="/design-and-architecture/" element={<IndDesignAndArchitecture />} />
              <Route path="/furniture-and-bedding/" element={<IndFurnitureBedding />} />
              <Route path="/furniture-and-bedding/" element={<IndFurnitureBedding />} />
              <Route path="/heating-cooling/" element={<IndHeatingCooling />} />
              <Route path="/secure-guest-casting/" element={<IndSecureGuestCasting />} />
              <Route path="/appliances/" element={<IndAppliances />} />
              {/* <Route path="/appliances-2/" element={<IndAppliances2 />} />
              <Route path="/appliances-3/" element={<IndAppliances3 />} />
              <Route path="/appliances-4/" element={<IndAppliances4 />} />
              <Route path="/appliances-5-webp-darker/" element={<IndAppliances5WebpDarker />} /> */}
              <Route path="/turn-key/" element={<IndTurnKey />} />
              <Route path="/bathrooms/" element={<IndBathrooms />} />
              <Route path="/kitchens/" element={<IndKitchen />} />


              {/* 2025129: Resource Routes using ADX Community Blog and Case Studies partial url*/}
              <Route path="/blogs/" element={<IndBlogs />} />
              <Route path="/case-studies/" element={<IndCaseStudies />} />
              <Route path="/case-studies/:id" element={<BlogItem />} />              
              <Route path="/blogs/:id" element={<BlogItem />} />

              {/* 27/8/26 */}
              {/* <Route path="/case-studies/:id/" element={<BlogItem />} />
              <Route path="/blogs/:id/" element={<BlogItem />} /> */}
              {/* end of 27/8/26 */}

              {/* Redirect old blog/casestudies URLs to new ones */}

              <Route path="/blog/:id" element={<BlogRedirect />} />
              <Route path="/casestudies/:id" element={<BlogRedirect />} />



              {/* 2025129: Resource Routes using ADX Community Blog and Case Studies partial url*/}
              {/* <Route path="/resources" element={<Resources />} />              */}
              {/* <Route path="/resources/blogs/:id" element={<BlogItem />} /> */}
              {/* to be removed after testing blog and casestudies */}
              {/* <Route path="/blog/" element={<IndBlogs />} />              
              <Route path="/blog/:id" element={<BlogItem />} />
              <Route path="/casestudies/" element={<IndCaseStudies />} />
              <Route path="/casestudies/:id" element={<BlogItem />} /> */}
              {/* end of to be removed */}


              {/* Showroom Routes */}
              {/* <Route path="/showroom" element={<Showroom />} /> */}
              {/* 27-08-26 */}
              <Route path="/showroom/" element={<Showroom />} />

              {/* Product Routes */}
              <Route path="/products" element={<MainCategoryGridShowcase />} />
              <Route path="/products/:category" element={<CategoryShowcaseGrid />} />
              <Route path="/products/:category/:subCategory" element={<ShowcaseGrid />} />
              <Route path="/products/:category/:subCategory/:showcase" element={<ShowcaseItemListShowcase />} />
              <Route path="/products/:category/:subCategory/:showcase/:slug" element={<ShowcaseItemDetail />} />
              <Route path="/all-products" element={<AllShowcaseItems />} />
              <Route path="/quote" element={<QuoteTablePage />} />

              {/* 24/11/25 */}
              {/* Footer React Routes */}
              {/* <Route path="/privacy-policy" element={<PrivacyPolicy />} /> */}
              <Route path="/terms-of-use" element={<TermsOfUse />} />
              <Route path="/retail-return-policy" element={<RetailReturnPolicy />} />
              <Route path="/platform-deliverezy-terms-conditions" element={<DeliveryEzyTermsConditions />} />
              {/* <Route path="/email-preferences" element={<EmailPreferenceForm />} /> */}

              {/* <Route path="/draft-404" element={<Draft404 />} /> */}




              {/* Dynamic Routes from Navigation */}
              {nav?.results.map((item, index) => (
                <React.Fragment key={`${item.url}-${index}`}>
                  {/* Sublinks */}
                  {item.sublinks?.map((subitem, subIndex) => (
                    <Route
                      key={`${subitem.url}-${subIndex}`}
                      path={`${item.url}/${subitem.url}`}
                      element={<RenderContentPage id={subitem.pageid} />}
                    />
                  ))}

                  {/* Child sublinks */}
                  {item.sublinks?.map((subitem, subIndex) =>
                    subitem.sublinks?.map((childitem, childIndex) => (
                      <Route
                        key={`${childitem.url}-${childIndex}`}
                        path={`${item.url}/${subitem.url}/${childitem.url}`}
                        element={<RenderContentPage id={childitem.pageid} />}
                      />
                    ))
                  )}
                </React.Fragment>
              ))}

              {/* Protected Customer Portal Routes */}
              <Route element={<PrivateRoute />}>
                <Route path="/customer-portal" element={<CustomerPortalLayout />}>
                  <Route path="" element={<CustomerPortalWelcome />} />
                  <Route path="invoices" element={<Invoices />} />
                  <Route path="orders" element={<Orders />} />
                  <Route path="pricelist" element={<PriceList />} />
                  <Route path="quotes" element={<Quotes />} />
                  <Route path="*" element={<NotFound />} />
                </Route>
              </Route>

              {/* 404 Fallback */}
              <Route path="*" element={<NotFound />} />
            </ReactRoutes>
          </Suspense>
        </div>
      </div>

      {/* Global Footer Components - Only show outside customer portal */}
      {!isCustomerPortal && (
        <Suspense fallback={<LoadingSpinner message="Loading..." />}>
          <EmailSubscribe />
          <Footer />
        </Suspense>
      )}
    </>
  );
}

const CustomerPortalLayout = () => {
  return (
    <Suspense fallback={<LoadingSpinner message="Loading Customer Portal..." />}>
      <SideMenu />
      <Outlet />
    </Suspense>
  );
};

